import {
  Button,
  Input,
  Icon,
  Avatar,
  Tag,
  Box,
  HighlightIcon,
} from '@dev-spendesk/grapes';
import classNames from 'classnames';
import React, { type MouseEventHandler } from 'react';

import { fallbackSupplierLogoSrc } from 'common/components/SupplierLogo';
import {
  hasIntegrationFileBasedExport,
  type AccountingSoftware,
} from 'modules/bookkeep/integration/status';
import { useTranslation } from 'src/core/common/hooks/useTranslation';

import { getAccountingIntegrationDetails } from '../../../../integrations/helper';

import './AccountingBaseInput.css';

const getAccountingIntegrationIcon = (
  logoPath: string,
  translatedI18nTitle: string,
): React.ReactNode => (
  <span className="flex items-center pl-8">
    <Avatar
      variant="square"
      src={logoPath}
      fallbackSrc={fallbackSupplierLogoSrc}
      text={translatedI18nTitle}
      size={24}
    />
  </span>
);

export function AccountingBaseInput({
  value,
  onClick,
  className,
}: {
  value: AccountingSoftware;
  onClick?: MouseEventHandler<HTMLElement>;
  className?: string;
}) {
  const { t } = useTranslation('global');

  const {
    i18nSelectTitle: i18nTitle,
    logoPath,
    i18nSelectType: i18nType,
  } = getAccountingIntegrationDetails(value);
  return (
    <Box
      data-testid="accounting-base-input"
      variant="secondary"
      className={classNames('flex flex-row justify-between', className)}
    >
      <div
        data-testid="accounting-base-input-input"
        className="flex flex-row items-center gap-16"
      >
        {hasIntegrationFileBasedExport(value) ? (
          <HighlightIcon name="table-cells" variant="forest" size={40} />
        ) : (
          getAccountingIntegrationIcon(logoPath, t(i18nTitle))
        )}
        <div className="ml-8 text-content-primary body-m">
          <span>{t(i18nTitle)}</span>
          &nbsp;
          <span className="text-content-secondary-bg-secondary">{t(i18nType)}</span>
        </div>
      </div>
      <Button
        variant="secondaryNeutral"
        text={t('bookkeep.integrations.settings.baseSelect.switch')}
        onClick={(event) => onClick && onClick(event)}
      />
    </Box>
  );
}

export function AccountingBaseToggle({
  value,
  isBeta,
}: {
  value: AccountingSoftware | 'noIntegration';
  isBeta: boolean;
}) {
  const { t } = useTranslation('global');

  if (value === 'noIntegration') {
    return (
      <Input
        className="AccountingBaseToggle"
        value=""
        type="text"
        fit="parent"
        placeholder={t('bookkeep.integrations.settings.baseSelect.placeholder')}
        rightAddon={
          <span className="AccountingBaseInput__rightIcon">
            <Icon color="currentColor" name="chevron-down" size="m" />
          </span>
        }
      />
    );
  }

  const { i18nSelectTitle: i18nTitle, logoPath } =
    getAccountingIntegrationDetails(value);
  return (
    <Input
      className="AccountingBaseToggle"
      value={t(i18nTitle)}
      type="text"
      fit="parent"
      placeholder={t('bookkeep.integrations.settings.baseSelect.placeholder')}
      leftAddon={getAccountingIntegrationIcon(logoPath, t(i18nTitle))}
      rightAddon={
        <>
          {isBeta && (
            <>
              &nbsp;
              <Tag variant="purple">{t('bookkeep.integrations.openBeta')}</Tag>
            </>
          )}

          <span className="AccountingBaseInput__rightIcon">
            <Icon color="currentColor" name="chevron-down" size="m" />
          </span>
        </>
      }
    />
  );
}
